




import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchAdminLiveTracking } from '../../../redux/slices/adminLiveTrackingSlice';
import { 
  initAdminLiveTrackingService, 
  disconnectAdminLiveTrackingService 
} from '../../../services/adminLiveTracking.service';

const LiveTrackingPage = () => {
  // ================= REDUX CONNECTION =================
  const dispatch = useDispatch();
  const { employees, loading, error } = useSelector(state => state.adminLiveTracking);
  
  // ================= STATE MANAGEMENT =================
  const [time, setTime] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filterStatus, setFilterStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showUpdates, setShowUpdates] = useState(true);
  const [realTimeUpdates, setRealTimeUpdates] = useState([]);
  
  // ================= USE EFFECTS =================
  
  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Initialize Redux data and socket connection
  useEffect(() => {
    // Fetch initial data
    dispatch(fetchAdminLiveTracking());
    
    // Initialize socket service
    initAdminLiveTrackingService(dispatch);
    
    return () => {
      // Cleanup socket connection
      disconnectAdminLiveTrackingService();
    };
  }, [dispatch]);

  // Auto-refresh tracking data
  useEffect(() => {
    if (!autoRefresh) return;
    
    const refreshTimer = setInterval(() => {
      dispatch(fetchAdminLiveTracking());
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshTimer);
  }, [autoRefresh, dispatch]);

  // Initialize real-time updates
  useEffect(() => {
    const mockUpdates = [
      { id: 1, type: 'punch_in', employeeName: 'Rahul Sharma', message: 'punched in', timestamp: new Date(Date.now() - 120000).toISOString() },
      { id: 2, type: 'status_change', employeeName: 'Priya Patel', message: 'joined a meeting', timestamp: new Date(Date.now() - 300000).toISOString() },
      { id: 3, type: 'activity', employeeName: 'Amit Kumar', message: 'started working on API', timestamp: new Date(Date.now() - 180000).toISOString() },
      { id: 4, type: 'break', employeeName: 'Neha Singh', message: 'went on break', timestamp: new Date(Date.now() - 720000).toISOString() },
      { id: 5, type: 'app_change', employeeName: 'Sanjay Verma', message: 'switched to email', timestamp: new Date(Date.now() - 480000).toISOString() }
    ];
    setRealTimeUpdates(mockUpdates);
  }, []);

  // ================= HELPER FUNCTIONS =================
  
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = 
      searchQuery === '' ||
      employee.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      employee.employeeId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (employee.department && employee.department.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (employee.currentActivity && employee.currentActivity.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = 
      filterStatus === 'All' || 
      employee.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch(status) {
      case 'ACTIVE': return '✅';
      case 'IDLE': return '😴';
      case 'MEETING': return '📅';
      case 'BREAK': return '☕';
      case 'OFFLINE': return '🔴';
      default: return '⏸️';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'ACTIVE': return '#10b981';
      case 'IDLE': return '#f59e0b';
      case 'MEETING': return '#3b82f6';
      case 'BREAK': return '#ef4444';
      case 'OFFLINE': return '#94a3b8';
      default: return '#94a3b8';
    }
  };

  const getAppIcon = (appName) => {
    if (!appName) return '🖥️';
    const app = appName.toLowerCase();
    if (app.includes('chrome') || app.includes('browser')) return '🌐';
    if (app.includes('vscode') || app.includes('code')) return '💻';
    if (app.includes('figma')) return '🎨';
    if (app.includes('zoom') || app.includes('meet')) return '📹';
    if (app.includes('outlook') || app.includes('gmail')) return '📧';
    if (app.includes('slack') || app.includes('teams')) return '💬';
    if (app.includes('postman')) return '📡';
    if (app.includes('excel') || app.includes('sheet')) return '📊';
    if (app.includes('word') || app.includes('docs')) return '📝';
    return '🖥️';
  };

  // const formatDuration = (seconds) => {
  //   if (!seconds || seconds === 0) return '0m';
  //   const hours = Math.floor(seconds / 3600);
  //   const minutes = Math.floor((seconds % 3600) / 60);
    
  //   if (hours > 0) {
  //     return `${hours}h ${minutes}m`;
  //   }
  //   return `${minutes}m`;
  // };


  
  const calculateProductivity = (employee) => {
    const workSeconds = employee.workSeconds || 0;
    const idleSeconds = employee.idleSeconds || 0;
    const total = workSeconds + idleSeconds;
    
    if (total === 0) return 0;
    return Math.round((workSeconds / total) * 100);
  };

  const getUpdateIcon = (type) => {
    switch(type) {
      case 'punch_in': return '👤';
      case 'punch_out': return '🚪';
      case 'status_change': return '🔄';
      case 'app_change': return '💻';
      case 'activity': return '🎯';
      case 'meeting': return '📅';
      case 'break': return '☕';
      default: return '📢';
    }
  };

  const getUpdateColor = (type) => {
    switch(type) {
      case 'punch_in': return '#10b981';
      case 'punch_out': return '#ef4444';
      case 'status_change': return '#3b82f6';
      case 'app_change': return '#8b5cf6';
      case 'activity': return '#f59e0b';
      default: return '#94a3b8';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const now = new Date();
    const updateTime = new Date(timestamp);
    const diffMs = now - updateTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const statusStats = {
    total: employees.length,
    working: employees.filter(e => e.status === 'ACTIVE').length,
    idle: employees.filter(e => e.status === 'IDLE').length,
    meeting: employees.filter(e => e.status === 'MEETING').length,
    break: employees.filter(e => e.status === 'BREAK').length,
    offline: employees.filter(e => e.status === 'OFFLINE').length,
  };

  const activityData = [
    { time: '9 AM', activity: 85 },
    { time: '10 AM', activity: 92 },
    { time: '11 AM', activity: 78 },
    { time: '12 PM', activity: 45 },
    { time: '1 PM', activity: 88 },
    { time: '2 PM', activity: 95 },
    { time: '3 PM', activity: 82 },
    { time: '4 PM', activity: 76 },
    { time: '5 PM', activity: 65 },
  ];

  const statusData = [
    { name: 'Working', value: statusStats.working, color: '#10b981' },
    { name: 'Idle', value: statusStats.idle, color: '#f59e0b' },
    { name: 'Meeting', value: statusStats.meeting, color: '#3b82f6' },
    { name: 'Break', value: statusStats.break, color: '#ef4444' },
    { name: 'Offline', value: statusStats.offline, color: '#94a3b8' },
  ];

  // ================= RENDER FUNCTIONS =================
  
  const renderStatusDistributionChart = () => {
    const total = statusData.reduce((sum, item) => sum + item.value, 0);
    const percentages = statusData.map(item => ({
      ...item,
      percentage: total > 0 ? Math.round((item.value / total) * 100) : 0
    }));

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '30px',
        flexWrap: 'wrap'
      }}>
        <div style={{
          width: '150px',
          height: '150px',
          borderRadius: '50%',
          background: `conic-gradient(${percentages.map((item, index) => 
            `${item.color} ${index === 0 ? '0%' : percentages.slice(0, index).reduce((a, b) => a + b.percentage, 0) + '%'} ` +
            `${percentages.slice(0, index + 1).reduce((a, b) => a + b.percentage, 0)}%`
          ).join(', ')})`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            background: '#1e293b',
            borderRadius: '50%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#f1f5f9' }}>{total}</span>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>Employees</span>
          </div>
        </div>
        
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {percentages.map((item, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#94a3b8'
            }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: item.color
              }}></div>
              <span>{item.name} ({item.value})</span>
              <span style={{ marginLeft: 'auto', color: '#f1f5f9' }}>{item.percentage}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderActivityTimeline = () => {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '10px'
      }}>
        {activityData.map((slot, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '60px',
              fontSize: '12px',
              color: '#94a3b8'
            }}>{slot.time}</div>
            <div style={{
              flex: 1,
              height: '20px',
              background: '#334155',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${slot.activity}%`,
                height: '100%',
                background: slot.activity > 70 ? '#10b981' : slot.activity > 40 ? '#3b82f6' : '#f59e0b',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            <div style={{
              width: '40px',
              textAlign: 'right',
              fontSize: '12px',
              fontWeight: '600',
              color: '#f1f5f9'
            }}>{slot.activity}%</div>
          </div>
        ))}
      </div>
    );
  };

  // const renderEmployeeCard = (employee) => {
  //   const productivity = calculateProductivity(employee);
    
  //   return (
  //     <div 
  //       key={employee.employeeId}
  //       className="employee-card"
  //       onClick={() => {
  //         setSelectedEmployee(employee);
  //         setShowDetailModal(true);
  //       }}
  //       style={{
  //         background: '#0f172a',
  //         borderRadius: '12px',
  //         padding: '20px',
  //         border: '1px solid #334155',
  //         cursor: 'pointer',
  //         transition: 'all 0.2s',
  //         position: 'relative',
  //         overflow: 'hidden'
  //       }}
  //       onMouseEnter={(e) => {
  //         e.currentTarget.style.transform = 'translateY(-4px)';
  //         e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
  //         e.currentTarget.style.borderColor = '#3b82f6';
  //       }}
  //       onMouseLeave={(e) => {
  //         e.currentTarget.style.transform = 'translateY(0)';
  //         e.currentTarget.style.boxShadow = 'none';
  //         e.currentTarget.style.borderColor = '#334155';
  //       }}
  //     >
  //       {/* Top gradient bar */}
  //       <div style={{
  //         position: 'absolute',
  //         top: 0,
  //         left: 0,
  //         right: 0,
  //         height: '4px',
  //         background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)'
  //       }}></div>

  //       {/* Card Header */}
  //       <div style={{
  //         display: 'flex',
  //         alignItems: 'flex-start',
  //         marginBottom: '20px',
  //         gap: '12px'
  //       }}>
  //         <div style={{ position: 'relative' }}>
  //           <div style={{
  //             width: '60px',
  //             height: '60px',
  //             borderRadius: '50%',
  //             background: getStatusColor(employee.status),
  //             border: `3px solid ${getStatusColor(employee.status)}50`,
  //             display: 'flex',
  //             alignItems: 'center',
  //             justifyContent: 'center',
  //             fontSize: '24px',
  //             fontWeight: '600',
  //             color: 'white'
  //           }}>
  //             {employee.name.charAt(0)}
  //           </div>
  //           <div style={{
  //             position: 'absolute',
  //             bottom: 0,
  //             right: 0,
  //             width: '14px',
  //             height: '14px',
  //             borderRadius: '50%',
  //             background: getStatusColor(employee.status),
  //             border: '2px solid #0f172a'
  //           }}></div>
  //         </div>
          
  //         <div style={{ flex: 1 }}>
  //           <h4 style={{
  //             fontSize: '18px',
  //             fontWeight: '600',
  //             margin: '0 0 4px 0',
  //             color: '#f1f5f9'
  //           }}>{employee.name}</h4>
  //           <p style={{
  //             color: '#94a3b8',
  //             fontSize: '12px',
  //             margin: '0 0 8px 0'
  //           }}>{employee.employeeId}</p>
  //           <div>
  //             <span style={{
  //               background: 'rgba(59, 130, 246, 0.2)',
  //               color: '#3b82f6',
  //               padding: '4px 8px',
  //               borderRadius: '12px',
  //               fontSize: '12px',
  //               fontWeight: '500'
  //             }}>{employee.department || 'Engineering'}</span>
  //           </div>
  //         </div>
          
  //         <div style={{
  //           padding: '6px 12px',
  //           borderRadius: '20px',
  //           fontSize: '12px',
  //           fontWeight: '500',
  //           display: 'flex',
  //           alignItems: 'center',
  //           gap: '4px',
  //           whiteSpace: 'nowrap',
  //           background: `${getStatusColor(employee.status)}20`,
  //           color: getStatusColor(employee.status)
  //         }}>
  //           {getStatusIcon(employee.status)} {employee.status}
  //         </div>
  //       </div>
        
  //       {/* Card Body */}
  //       <div style={{ marginBottom: '20px' }}>
  //         <div style={{
  //           display: 'flex',
  //           justifyContent: 'space-between',
  //           alignItems: 'center',
  //           marginBottom: '12px',
  //           fontSize: '14px'
  //         }}>
  //           <span style={{ color: '#94a3b8', minWidth: '120px' }}>Current Activity:</span>
  //           <span style={{ color: '#f1f5f9', fontWeight: '500', textAlign: 'right', flex: 1 }}>
  //             {employee.currentActivity || 'No activity'}
  //           </span>
  //         </div>
          
  //         <div style={{
  //           display: 'flex',
  //           justifyContent: 'space-between',
  //           alignItems: 'center',
  //           marginBottom: '12px',
  //           fontSize: '14px'
  //         }}>
  //           <span style={{ color: '#94a3b8', minWidth: '120px' }}>Application:</span>
  //           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f1f5f9', fontWeight: '500' }}>
  //             <span style={{ fontSize: '16px' }}>{getAppIcon(employee.appName)}</span>
  //             <span>{employee.appName || 'Not available'}</span>
  //           </div>
  //         </div>
          
  //         <div style={{
  //           display: 'flex',
  //           justifyContent: 'space-between',
  //           alignItems: 'center',
  //           marginBottom: '12px',
  //           fontSize: '14px'
  //         }}>
  //           <span style={{ color: '#94a3b8', minWidth: '120px' }}>Active Time:</span>
  //           <span style={{ color: '#10b981', fontWeight: '600' }}>
  //             {formatDuration(employee.workSeconds)}
  //           </span>
  //         </div>
          
  //         <div style={{
  //           display: 'flex',
  //           justifyContent: 'space-between',
  //           alignItems: 'center',
  //           marginBottom: '12px',
  //           fontSize: '14px'
  //         }}>
  //           <span style={{ color: '#94a3b8', minWidth: '120px' }}>Idle Time:</span>
  //           <span style={{ color: '#f1f5f9', fontWeight: '500' }}>
  //             {formatDuration(employee.idleSeconds)}
  //           </span>
  //         </div>
          
  //         <div style={{ marginTop: '16px' }}>
  //           <div style={{
  //             display: 'flex',
  //             justifyContent: 'space-between',
  //             alignItems: 'center',
  //             marginBottom: '8px',
  //             fontSize: '14px'
  //           }}>
  //             <span style={{ color: '#94a3b8' }}>Productivity:</span>
  //             <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>{productivity}%</span>
  //           </div>
  //           <div style={{
  //             height: '8px',
  //             borderRadius: '4px',
  //             background: '#334155',
  //             overflow: 'hidden',
  //             position: 'relative'
  //           }}>
  //             <div style={{
  //               height: '100%',
  //               borderRadius: '4px',
  //               width: `${productivity}%`,
  //               background: productivity > 70 ? '#10b981' : 
  //                        productivity > 40 ? '#f59e0b' : '#ef4444',
  //               transition: 'width 0.3s ease'
  //             }}></div>
  //           </div>
  //         </div>
  //       </div>
        
  //       {/* Card Footer */}
  //       <div style={{
  //         display: 'flex',
  //         justifyContent: 'space-between',
  //         alignItems: 'center',
  //         paddingTop: '16px',
  //         borderTop: '1px solid #334155'
  //       }}>
  //         <div style={{
  //           display: 'flex',
  //           flexDirection: 'column',
  //           gap: '4px'
  //         }}>
  //           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
  //             <span style={{ color: '#94a3b8' }}>Punch In:</span>
  //             <span style={{ color: '#f1f5f9', fontWeight: '500' }}>
  //               {employee.startAt ? 
  //                 new Date(employee.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
  //                 'Not punched'
  //               }
  //             </span>
  //           </div>
  //           <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
  //             <span style={{ color: '#94a3b8' }}>Today:</span>
  //             <span style={{ color: '#f1f5f9', fontWeight: '500' }}>
  //               {formatDuration((employee.workSeconds || 0) + (employee.idleSeconds || 0))}
  //             </span>
  //           </div>
  //         </div>
          
  //         <button 
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             setSelectedEmployee(employee);
  //             setShowDetailModal(true);
  //           }}
  //           style={{
  //             background: 'transparent',
  //             color: '#3b82f6',
  //             border: '1px solid #3b82f6',
  //             padding: '6px 12px',
  //             borderRadius: '6px',
  //             cursor: 'pointer',
  //             fontSize: '12px',
  //             fontWeight: '500',
  //             transition: 'all 0.2s'
  //           }}
  //           onMouseEnter={(e) => {
  //             e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
  //           }}
  //           onMouseLeave={(e) => {
  //             e.currentTarget.style.background = 'transparent';
  //           }}
  //         >
  //           View Details →
  //         </button>
  //       </div>
  //     </div>
  //   );
  // };

// ✅ FIXED: formatDuration function to show total time correctly
const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

// ✅ FIXED: Update employee card to show TOTAL time
const renderEmployeeCard = (employee) => {
  const productivity = calculateProductivity(employee);
  
  // ✅ CRITICAL: Calculate total time from workSeconds + idleSeconds
  const totalSeconds = (employee.workSeconds || 0) + (employee.idleSeconds || 0);
  const totalTime = formatDuration(totalSeconds);
  
  return (
    <div 
      key={employee.employeeId}
      className="employee-card"
      onClick={() => {
        setSelectedEmployee(employee);
        setShowDetailModal(true);
      }}
      style={{
        background: '#0f172a',
        borderRadius: '12px',
        padding: '20px',
        border: '1px solid #334155',
        cursor: 'pointer',
        transition: 'all 0.2s',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.3)';
        e.currentTarget.style.borderColor = '#3b82f6';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.borderColor = '#334155';
      }}
    >
      {/* Top gradient bar */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #8b5cf6, #3b82f6)'
      }}></div>

      {/* Card Header */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: '20px',
        gap: '12px'
      }}>
        <div style={{ position: 'relative' }}>
          <div style={{
            width: '60px',
            height: '60px',
            borderRadius: '50%',
            background: getStatusColor(employee.status),
            border: `3px solid ${getStatusColor(employee.status)}50`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '24px',
            fontWeight: '600',
            color: 'white'
          }}>
            {employee.name.charAt(0)}
          </div>
          <div style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            background: getStatusColor(employee.status),
            border: '2px solid #0f172a'
          }}></div>
        </div>
        
        <div style={{ flex: 1 }}>
          <h4 style={{
            fontSize: '18px',
            fontWeight: '600',
            margin: '0 0 4px 0',
            color: '#f1f5f9'
          }}>{employee.name}</h4>
          <p style={{
            color: '#94a3b8',
            fontSize: '12px',
            margin: '0 0 8px 0'
          }}>{employee.employeeId}</p>
          <div>
            <span style={{
              background: 'rgba(59, 130, 246, 0.2)',
              color: '#3b82f6',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500'
            }}>{employee.department || 'Engineering'}</span>
          </div>
        </div>
        
        <div style={{
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          whiteSpace: 'nowrap',
          background: `${getStatusColor(employee.status)}20`,
          color: getStatusColor(employee.status)
        }}>
          {getStatusIcon(employee.status)} {employee.status}
        </div>
      </div>
      
      {/* Card Body */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          fontSize: '14px'
        }}>
          <span style={{ color: '#94a3b8', minWidth: '120px' }}>Current Activity:</span>
          <span style={{ color: '#f1f5f9', fontWeight: '500', textAlign: 'right', flex: 1 }}>
            {employee.currentActivity || 'No activity'}
          </span>
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          fontSize: '14px'
        }}>
          <span style={{ color: '#94a3b8', minWidth: '120px' }}>Application:</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f1f5f9', fontWeight: '500' }}>
            <span style={{ fontSize: '16px' }}>{getAppIcon(employee.appName)}</span>
            <span>{employee.appName || 'Not available'}</span>
          </div>
        </div>
        
        {/* ✅ FIXED: Show TOTAL TIME prominently */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          fontSize: '14px',
          padding: '12px',
          background: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }}>
          <span style={{ color: '#3b82f6', minWidth: '120px', fontWeight: '600' }}>⏱️ Total Time Today:</span>
          <span style={{ color: '#3b82f6', fontWeight: '700', fontSize: '16px' }}>
            {totalTime}
          </span>
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          fontSize: '14px'
        }}>
          <span style={{ color: '#94a3b8', minWidth: '120px' }}>Active Time:</span>
          <span style={{ color: '#10b981', fontWeight: '600' }}>
            {formatDuration(employee.workSeconds)}
          </span>
        </div>
        
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '12px',
          fontSize: '14px'
        }}>
          <span style={{ color: '#94a3b8', minWidth: '120px' }}>Idle Time:</span>
          <span style={{ color: '#f1f5f9', fontWeight: '500' }}>
            {formatDuration(employee.idleSeconds)}
          </span>
        </div>
        
        <div style={{ marginTop: '16px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '8px',
            fontSize: '14px'
          }}>
            <span style={{ color: '#94a3b8' }}>Productivity:</span>
            <span style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>{productivity}%</span>
          </div>
          <div style={{
            height: '8px',
            borderRadius: '4px',
            background: '#334155',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              height: '100%',
              borderRadius: '4px',
              width: `${productivity}%`,
              background: productivity > 70 ? '#10b981' : 
                       productivity > 40 ? '#f59e0b' : '#ef4444',
              transition: 'width 0.3s ease'
            }}></div>
          </div>
        </div>
      </div>
      
      {/* Card Footer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        borderTop: '1px solid #334155'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <span style={{ color: '#94a3b8' }}>Punch In:</span>
            <span style={{ color: '#f1f5f9', fontWeight: '500' }}>
              {employee.startAt ? 
                new Date(employee.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
                'Not punched'
              }
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
            <span style={{ color: '#94a3b8' }}>Today:</span>
            <span style={{ color: '#f1f5f9', fontWeight: '500' }}>
              {totalTime}
            </span>
          </div>
        </div>
        
        <button 
          onClick={(e) => {
            e.stopPropagation();
            setSelectedEmployee(employee);
            setShowDetailModal(true);
          }}
          style={{
            background: 'transparent',
            color: '#3b82f6',
            border: '1px solid #3b82f6',
            padding: '6px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          View Details →
        </button>
      </div>
    </div>
  );
};

// ✅ Note: Add this to your existing LiveTrackingPage component
// Replace the existing renderEmployeeCard function with this one

  const renderEmployeeDetailModal = () => {
    if (!selectedEmployee || !showDetailModal) return null;
    
    const productivityScore = calculateProductivity(selectedEmployee);
    
    return (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px',
          backdropFilter: 'blur(4px)'
        }}
        onClick={() => setShowDetailModal(false)}
      >
        <div 
          style={{
            background: '#0f172a',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflowY: 'auto',
            border: '1px solid #334155',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px',
            borderBottom: '1px solid #334155',
            position: 'sticky',
            top: 0,
            background: '#0f172a',
            zIndex: 10,
            borderRadius: '16px 16px 0 0'
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '24px', color: '#f1f5f9' }}>
                {selectedEmployee.name}'s Activity Details
              </h2>
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: '4px 0 0 0' }}>
                Employee ID: {selectedEmployee.employeeId}
              </p>
            </div>
            <button 
              onClick={() => setShowDetailModal(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                fontSize: '24px',
                cursor: 'pointer',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#334155';
                e.currentTarget.style.color = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div style={{ padding: '24px' }}>
            {/* Employee Overview */}
            <div style={{
              background: '#1e293b',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              border: '1px solid #334155'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                marginBottom: '24px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: getStatusColor(selectedEmployee.status),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '32px',
                  fontWeight: '600',
                  color: 'white',
                  flexShrink: 0
                }}>
                  {selectedEmployee.name.charAt(0)}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 4px 0', fontSize: '24px', color: '#f1f5f9' }}>
                    {selectedEmployee.name}
                  </h3>
                  <p style={{ color: '#3b82f6', fontWeight: '500', margin: '0 0 4px 0' }}>
                    {selectedEmployee.role || 'Software Engineer'}
                  </p>
                  <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                    {selectedEmployee.department || 'Engineering Department'}
                  </p>
                </div>
                <div style={{
                  padding: '12px 20px',
                  borderRadius: '24px',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  background: `${getStatusColor(selectedEmployee.status)}20`,
                  color: getStatusColor(selectedEmployee.status)
                }}>
                  {getStatusIcon(selectedEmployee.status)} {selectedEmployee.status}
                </div>
              </div>

              {/* Stats Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '16px'
              }}>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>Punch In</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#f1f5f9' }}>
                    {selectedEmployee.startAt ? 
                      new Date(selectedEmployee.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
                      '--:--'
                    }
                  </div>
                </div>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>Active Time</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                    {formatDuration(selectedEmployee.workSeconds)}
                  </div>
                </div>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>Idle Time</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#f59e0b' }}>
                    {formatDuration(selectedEmployee.idleSeconds)}
                  </div>
                </div>
                <div style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px solid #334155', textAlign: 'center' }}>
                  <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '8px', textTransform: 'uppercase' }}>Productivity</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: productivityScore > 70 ? '#10b981' : '#f59e0b' }}>
                    {productivityScore}%
                  </div>
                </div>
              </div>
            </div>

            {/* Current Activity */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', color: '#f1f5f9', margin: '0 0 16px 0' }}>🎯 Current Activity</h3>
              <div style={{
                background: '#1e293b',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #334155'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '20px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <span style={{ fontSize: '32px' }}>{getAppIcon(selectedEmployee.appName)}</span>
                    <div>
                      <strong style={{ display: 'block', color: '#f1f5f9', fontSize: '14px', marginBottom: '4px' }}>
                        {selectedEmployee.appName || 'Unknown Application'}
                      </strong>
                      <p style={{ margin: 0, color: '#94a3b8', fontSize: '12px' }}>
                        {selectedEmployee.currentActivity || 'No active task'}
                      </p>
                    </div>
                  </div>
                  <span style={{
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#3b82f6',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}>
                    {formatDuration((selectedEmployee.workSeconds || 0) + (selectedEmployee.idleSeconds || 0))}
                  </span>
                </div>
                <div style={{ marginTop: '20px' }}>
                  <div style={{ height: '8px', background: '#334155', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
                    <div style={{
                      height: '100%',
                      borderRadius: '4px',
                      width: `${Math.min(productivityScore, 100)}%`,
                      background: getStatusColor(selectedEmployee.status),
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8' }}>
                    <span>Start</span>
                    <span>Now</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Time Breakdown */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', color: '#f1f5f9', margin: '0 0 16px 0' }}>⏱️ Today's Time Breakdown</h3>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {[
                  { label: 'Active Work', value: selectedEmployee.workSeconds || 0, color: '#10b981' },
                  { label: 'Idle Time', value: selectedEmployee.idleSeconds || 0, color: '#f59e0b' },
                  { label: 'Meetings', value: selectedEmployee.meetingSeconds || 0, color: '#3b82f6' },
                  { label: 'Breaks', value: selectedEmployee.breakSeconds || 0, color: '#ef4444' }
                ].map((item, index) => {
                  const total = (selectedEmployee.workSeconds || 0) + (selectedEmployee.idleSeconds || 0) + 
                              (selectedEmployee.meetingSeconds || 0) + (selectedEmployee.breakSeconds || 0);
                  const percentage = total > 0 ? Math.round((item.value / total) * 100) : 0;
                  
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ width: '120px', color: '#94a3b8', fontSize: '14px' }}>{item.label}</div>
                      <div style={{ flex: 1, height: '12px', background: '#334155', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%',
                          borderRadius: '6px',
                          width: `${percentage}%`,
                          background: item.color,
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                      <div style={{ width: '80px', textAlign: 'right', fontWeight: '600', color: '#f1f5f9', fontSize: '14px' }}>
                        {formatDuration(item.value)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Activity History - Placeholder */}
            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', color: '#f1f5f9', margin: '0 0 16px 0' }}>📋 Recent Activity History</h3>
              <div style={{
                background: '#1e293b',
                borderRadius: '12px',
                border: '1px solid #334155',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                  Activity history will be populated from API data
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div>
              <h3 style={{ fontSize: '18px', color: '#f1f5f9', margin: '0 0 16px 0' }}>📊 Performance Metrics</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {[
                  { icon: '🎯', title: 'Productivity Score', value: `${productivityScore}%`, subtitle: "Today's performance" },
                  { icon: '📅', title: 'Attendance Rate', value: '95%', subtitle: 'This month' },
                  { icon: '⏱️', title: 'Avg. Active Hours', value: '7h 30m', subtitle: 'Last 30 days' },
                  { icon: '📈', title: 'Avg. Productivity', value: '78%', subtitle: 'Monthly average' }
                ].map((metric, index) => (
                  <div key={index} style={{
                    background: '#1e293b',
                    borderRadius: '12px',
                    padding: '20px',
                    border: '1px solid #334155',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <div style={{ fontSize: '32px', opacity: 0.9 }}>{metric.icon}</div>
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#94a3b8' }}>{metric.title}</h4>
                      <p style={{ fontSize: '24px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 4px 0' }}>
                        {metric.value}
                      </p>
                      <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>{metric.subtitle}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px',
            borderTop: '1px solid #334155',
            background: '#0f172a',
            borderRadius: '0 0 16px 16px',
            position: 'sticky',
            bottom: 0,
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <button 
              onClick={() => setShowDetailModal(false)}
              style={{
                background: 'transparent',
                color: '#94a3b8',
                border: '1px solid #334155',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#334155';
                e.currentTarget.style.color = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              Close
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <button style={{
                background: 'transparent',
                color: '#94a3b8',
                border: '1px solid #334155',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#334155';
                e.currentTarget.style.color = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }}>
                💬 Message
              </button>
              <button style={{
                background: 'transparent',
                color: '#94a3b8',
                border: '1px solid #334155',
                padding: '8px 16px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#334155';
                e.currentTarget.style.color = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#94a3b8';
              }}>
                📷 Screenshots
              </button>
              <button style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#3b82f6';
              }}>
                Export Details
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRealTimeUpdates = () => {
    return (
      <div style={{
        background: '#1e293b',
        borderRadius: '12px',
        padding: '20px',
        marginTop: '24px',
        border: '1px solid #334155'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: showUpdates ? '16px' : '0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '18px', color: '#f1f5f9' }}>🔄 Real-time Updates</h3>
            <span style={{
              background: '#3b82f6',
              color: 'white',
              fontSize: '12px',
              fontWeight: '600',
              padding: '2px 8px',
              borderRadius: '12px',
              minWidth: '20px',
              textAlign: 'center'
            }}>
              {realTimeUpdates.length}
            </span>
          </div>
          <button 
            onClick={() => setShowUpdates(!showUpdates)}
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: '1px solid #334155',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#334155';
              e.currentTarget.style.color = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            {showUpdates ? 'Hide' : 'Show'} Updates
          </button>
        </div>

        {showUpdates && (
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {realTimeUpdates.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {realTimeUpdates.map((update) => (
                  <div 
                    key={update.id}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      padding: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      borderLeft: `3px solid ${getUpdateColor(update.type)}`,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                    }}
                  >
                    <div style={{ fontSize: '20px', flexShrink: 0, color: getUpdateColor(update.type) }}>
                      {getUpdateIcon(update.type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#f1f5f9', fontSize: '14px', lineHeight: '1.4', marginBottom: '4px' }}>
                        <strong style={{ color: '#3b82f6' }}>{update.employeeName}</strong> {update.message}
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8' }}>
                        <span>{formatTime(update.timestamp)}</span>
                        <span>{update.type.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}>📭</div>
                <p style={{ color: '#94a3b8', margin: '0 0 8px 0' }}>No recent updates</p>
                <small style={{ color: '#64748b', fontSize: '12px' }}>Updates will appear here in real-time</small>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // ================= LOADING STATE =================
  if (loading && employees.length === 0) {
    return (
      <div style={{
        padding: '20px',
        background: '#0f172a',
        minHeight: '100vh',
        color: '#f1f5f9',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
          <h2 style={{ color: '#f1f5f9' }}>Loading Employee Data...</h2>
          <p style={{ color: '#94a3b8' }}>Fetching real-time tracking information</p>
        </div>
      </div>
    );
  }

  // ================= MAIN RENDER =================
  
  return (
    <div style={{
      padding: '20px',
      background: '#0f172a',
      minHeight: '100vh',
      color: '#f1f5f9'
    }}>
      {/* Dashboard Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div style={{ flex: 1, minWidth: '300px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#f1f5f9',
            margin: '0 0 8px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '32px' }}>📡</span>
            Employee Tracking Dashboard
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
            Real-time monitoring • {filteredEmployees.length} employees online • 
            Last updated: {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', color: '#94a3b8' }}>
                <input 
                  type="checkbox" 
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  style={{ display: 'none' }}
                />
                <div style={{
                  width: '18px',
                  height: '18px',
                  border: '2px solid #475569',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s'
                }}>
                  {autoRefresh && <span style={{ color: 'white', fontSize: '12px' }}>✓</span>}
                </div>
                <span>Auto Refresh (30s)</span>
              </label>
            </div>
            
            <div style={{ display: 'flex', background: '#1e293b', borderRadius: '8px', padding: '4px' }}>
              <button 
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  background: viewMode === 'grid' ? '#334155' : 'transparent',
                  color: viewMode === 'grid' ? '#f1f5f9' : '#94a3b8',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
                onClick={() => setViewMode('grid')}
              >
                ▦ Grid
              </button>
              <button 
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  background: viewMode === 'list' ? '#334155' : 'transparent',
                  color: viewMode === 'list' ? '#f1f5f9' : '#94a3b8',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '14px',
                  transition: 'all 0.2s'
                }}
                onClick={() => setViewMode('list')}
              >
                ≡ List
              </button>
            </div>
            
            <button 
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#3b82f6';
              }}
              onClick={() => dispatch(fetchAdminLiveTracking())}
            >
              <span>🔄</span>
              Refresh Now
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {[
          { icon: '👥', value: statusStats.total, label: 'Total Employees', trend: `${employees.filter(e => e.status !== 'OFFLINE').length} online`, color: '#8b5cf6' },
          { icon: '✅', value: statusStats.working, label: 'Productive', trend: 'Active now', color: '#10b981' },
          { icon: '😴', value: statusStats.idle, label: 'Idle', trend: 'Need attention', color: '#f59e0b' },
          { icon: '📊', value: '78%', label: 'Avg Productivity', trend: '+2% from yesterday', color: '#3b82f6' }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#1e293b',
            borderRadius: '12px',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            border: '1px solid #334155',
            borderLeft: `4px solid ${stat.color}`,
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{ fontSize: '32px', opacity: 0.9 }}>{stat.icon}</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '32px', fontWeight: '700', margin: '0 0 4px 0', color: '#f1f5f9' }}>
                {stat.value}
              </h3>
              <p style={{ color: '#94a3b8', fontSize: '14px', margin: '0 0 8px 0' }}>{stat.label}</p>
              <div style={{ fontSize: '12px', color: stat.color }}>{stat.trend}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters & Search */}
      <div style={{
        background: '#1e293b',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        border: '1px solid #334155'
      }}>
        <div style={{ position: 'relative', marginBottom: '16px' }}>
          <span style={{
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#94a3b8',
            fontSize: '18px'
          }}>🔍</span>
          <input 
            type="text" 
            style={{
              width: '100%',
              padding: '12px 48px 12px 48px',
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '8px',
              color: '#f1f5f9',
              fontSize: '14px',
              transition: 'border-color 0.2s'
            }}
            placeholder="Search employees by name, ID, department, or activity..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={(e) => {
              e.target.style.borderColor = '#3b82f6';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#334155';
            }}
          />
          {searchQuery && (
            <button 
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: '#94a3b8',
                cursor: 'pointer',
                fontSize: '18px',
                padding: 0,
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setSearchQuery('')}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#f1f5f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#94a3b8';
              }}
            >
              ✕
            </button>
          )}
        </div>
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ color: '#94a3b8', fontSize: '14px', whiteSpace: 'nowrap' }}>Status Filter:</label>
            <select 
              style={{
                padding: '8px 12px',
                background: '#0f172a',
                border: '1px solid #334155',
                borderRadius: '6px',
                color: '#f1f5f9',
                fontSize: '14px',
                minWidth: '150px',
                cursor: 'pointer'
              }}
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="ACTIVE">Working ✅</option>
              <option value="IDLE">Idle 😴</option>
              <option value="MEETING">Meeting 📅</option>
              <option value="BREAK">Break ☕</option>
              <option value="OFFLINE">Offline 🔴</option>
            </select>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ color: '#94a3b8', fontSize: '14px', whiteSpace: 'nowrap' }}>Sort By:</label>
            <select style={{
              padding: '8px 12px',
              background: '#0f172a',
              border: '1px solid #334155',
              borderRadius: '6px',
              color: '#f1f5f9',
              fontSize: '14px',
              minWidth: '150px',
              cursor: 'pointer'
            }}>
              <option value="name">Name A-Z</option>
              <option value="productivity">Productivity</option>
              <option value="time">Active Time</option>
              <option value="status">Status</option>
            </select>
          </div>
          
          <button 
            style={{
              background: 'transparent',
              color: '#94a3b8',
              border: '1px solid #334155',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              marginLeft: 'auto'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#334155';
              e.currentTarget.style.color = '#f1f5f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#94a3b8';
            }}
          >
            <span>📥</span>
            Export Report
          </button>
        </div>
      </div>

      {/* Charts Section */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '20px',
        marginBottom: '24px'
      }}>
        {/* Activity Timeline */}
        <div style={{
          background: '#1e293b',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #334155'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '18px', margin: 0, color: '#f1f5f9' }}>📈 Activity Timeline (Today)</h3>
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Peak productive hours</span>
          </div>
          {renderActivityTimeline()}
        </div>
        
        {/* Status Distribution */}
        <div style={{
          background: '#1e293b',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #334155'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h3 style={{ fontSize: '18px', margin: 0, color: '#f1f5f9' }}>📊 Status Distribution</h3>
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>Current employee status</span>
          </div>
          {renderStatusDistributionChart()}
        </div>
      </div>

      {/* Employees Section */}
      <div style={{
        background: '#1e293b',
        borderRadius: '12px',
        padding: '24px',
        border: '1px solid #334155'
      }}>
        <div style={{ marginBottom: '20px' }}>
          <h2 style={{ fontSize: '24px', margin: '0 0 8px 0', color: '#f1f5f9' }}>👥 Employee Tracking</h2>
          <span style={{ color: '#94a3b8', fontSize: '14px' }}>
            {filteredEmployees.length} employees found • {filterStatus !== 'All' ? `Filtered by: ${filterStatus}` : 'Showing all statuses'}
          </span>
        </div>
        
        {/* Employees Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(350px, 1fr))' : '1fr',
          gap: '20px'
        }}>
          {filteredEmployees.length > 0 ? (
            filteredEmployees.map(renderEmployeeCard)
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.5 }}>🔍</div>
              <h3 style={{ fontSize: '20px', color: '#f1f5f9', margin: '0 0 8px 0' }}>No employees found</h3>
              <p style={{ color: '#94a3b8', margin: '0 0 20px 0' }}>Try adjusting your search or filter criteria</p>
              <button 
                style={{
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onClick={() => {
                  setSearchQuery('');
                  setFilterStatus('All');
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Real-time Updates */}
      {renderRealTimeUpdates()}

      {/* Employee Detail Modal */}
      {renderEmployeeDetailModal()}
    </div>
  );
};

export default LiveTrackingPage;